const EXIF_ASCII = 0x45786966;
const TAG_DATE_TIME = 0x0132;
const TAG_DATE_TIME_ORIGINAL = 0x9003;
const TAG_EXIF_POINTER = 0x8769;
const TYPE_ASCII = 2;
const TYPE_LONG = 4;
const HEAD_BYTES = 256 * 1024;

const TYPE_SIZES: Record<number, number> = {
  1: 1,
  2: 1,
  3: 2,
  4: 4,
  5: 8,
  7: 1,
  9: 4,
  10: 8,
};

const EXIF_DATE = /^(\d{4}):(\d{2}):(\d{2})[ T](\d{2}):(\d{2}):(\d{2})/;

export async function readTakenAt(file: File): Promise<Date | undefined> {
  if (file.type !== 'image/jpeg') return undefined;

  try {
    return parseTakenAt(await file.slice(0, HEAD_BYTES).arrayBuffer());
  } catch {
    return undefined;
  }
}

export function parseTakenAt(buffer: ArrayBuffer): Date | undefined {
  const view = new DataView(buffer);
  const tiff = findExifStart(view);
  return tiff === undefined ? undefined : readCaptureDate(view, tiff);
}

function findExifStart(view: DataView): number | undefined {
  if (view.byteLength < 4 || view.getUint16(0) !== 0xffd8) return undefined;

  let offset = 2;
  while (offset + 4 <= view.byteLength) {
    if (view.getUint8(offset) !== 0xff) return undefined;

    const marker = view.getUint8(offset + 1);
    if (marker === 0x01 || (marker >= 0xd0 && marker <= 0xd9)) {
      offset += 2;
      continue;
    }
    if (marker === 0xda) return undefined;

    const length = view.getUint16(offset + 2);
    if (length < 2) return undefined;
    const body = offset + 4;

    if (
      marker === 0xe1 &&
      body + 6 <= view.byteLength &&
      view.getUint32(body) === EXIF_ASCII &&
      view.getUint16(body + 4) === 0
    ) {
      return body + 6;
    }

    offset = body + length - 2;
  }

  return undefined;
}

interface Entry {
  type: number;
  count: number;
  valueAt: number;
}

function findEntry(
  view: DataView,
  tiff: number,
  ifd: number,
  tag: number,
  little: boolean,
): Entry | undefined {
  if (ifd + 2 > view.byteLength) return undefined;
  const entries = view.getUint16(ifd, little);

  for (let index = 0; index < entries; index += 1) {
    const at = ifd + 2 + index * 12;
    if (at + 12 > view.byteLength) return undefined;
    if (view.getUint16(at, little) !== tag) continue;

    const type = view.getUint16(at + 2, little);
    const count = view.getUint32(at + 4, little);
    const bytes = (TYPE_SIZES[type] ?? 0) * count;
    const valueAt = bytes > 4 ? tiff + view.getUint32(at + 8, little) : at + 8;

    return { type, count, valueAt };
  }

  return undefined;
}

function readAscii(
  view: DataView,
  tiff: number,
  ifd: number,
  tag: number,
  little: boolean,
): string | undefined {
  const entry = findEntry(view, tiff, ifd, tag, little);
  if (!entry || entry.type !== TYPE_ASCII || entry.count < 2) return undefined;
  if (entry.valueAt + entry.count > view.byteLength) return undefined;

  let text = '';
  for (let index = 0; index < entry.count - 1; index += 1) {
    const code = view.getUint8(entry.valueAt + index);
    if (code === 0) break;
    text += String.fromCharCode(code);
  }

  return text;
}

function readCaptureDate(view: DataView, tiff: number): Date | undefined {
  if (tiff + 8 > view.byteLength) return undefined;

  const order = view.getUint16(tiff);
  if (order !== 0x4949 && order !== 0x4d4d) return undefined;
  const little = order === 0x4949;

  const ifd0 = tiff + view.getUint32(tiff + 4, little);
  const pointer = findEntry(view, tiff, ifd0, TAG_EXIF_POINTER, little);
  const exifIfd =
    pointer?.type === TYPE_LONG
      ? tiff + view.getUint32(pointer.valueAt, little)
      : undefined;

  const raw =
    (exifIfd === undefined
      ? undefined
      : readAscii(view, tiff, exifIfd, TAG_DATE_TIME_ORIGINAL, little)) ??
    readAscii(view, tiff, ifd0, TAG_DATE_TIME, little);

  return raw ? parseExifDate(raw) : undefined;
}

function parseExifDate(raw: string): Date | undefined {
  const match = EXIF_DATE.exec(raw);
  if (!match) return undefined;

  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const hour = Number(match[4]);
  const minute = Number(match[5]);
  const second = Number(match[6]);

  if (year < 1900 || month < 1 || month > 12 || day < 1 || day > 31) return undefined;

  const date = new Date(year, month - 1, day, hour, minute, second);
  return Number.isNaN(date.getTime()) ? undefined : date;
}
